import { Injectable } from '@angular/core';
import {
  Observable,
  of,
  throwError
} from 'rxjs';
import {
  RuleDTO,
  RuleExecutionTypes,
  RuleStates,
  RuleTypes
} from '../../api/plyca-dtos-v2';
import { procedureComponent } from '../procedures/procedure.component';
import { PlycaDataService } from '../../../services/plyca.service';
import { Configuration } from '../../conf/app.constants';

import { TaskService } from '../../tasks/task.service';
import { Router } from '@angular/router';
import { getSimpleIdentifierFromString, Link, ActionDTO, ActionTypeDTO } from '../../api/plyca-dtos';
import { map, flatMap, catchError } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { UIService } from '../../ui.service';
import { TranslateService } from '@ngx-translate/core';
import { PlycaErrorHandler } from '../../../http/PlycaErrorHandler';
import { DialogService, DynamicDialogConfig } from 'primeng-lts/dynamicdialog';

@Injectable()
export class RuleService {
  constructor(
    public dialogService: DialogService,
    public router: Router,
    public plycaService: PlycaDataService,
    private _http: HttpClient,
    public configuration: Configuration,
    public taskService: TaskService,
    public uiService: UIService,
    private plycaErrorHandler: PlycaErrorHandler,
    public translate: TranslateService
  ) { }

  filterRulesBySeverity(
    rules: Array<RuleDTO>,
    severity: string | Array<string>
  ): Array<RuleDTO> {
    let filters: Array<string>;
    if (typeof severity === 'string') {
      filters = [severity];
    } else {
      filters = severity;
    }
    return rules.filter(rule => {
      let finded = filters.find(filter => filter === rule.severity);
      return finded !== undefined;
    });
  }

  isRuleExecutable(rule: RuleDTO): boolean {
    // La regla es ejecutable si cumple estas condiciones:
    // 1. Es manual
    // 2. Tiene type (por ejemplo los cambios de fase que no se cumplen todavia no lo tienen)
    // 3. Es state es pending
    let ret: boolean;
    ret = rule.executionType === RuleExecutionTypes.MANUAL; // 1
    // ret = ret && rule.type !== undefined; // 2
    ret = ret && rule.state === RuleStates.PENDING;
    //    ret = ret && (rule.type !== RuleTypes.TASK || rule.type === RuleTypes.TASK && !rule.locked);
    //    ret = ret && !rule.locked;
    // console.log('COMPROBANDO SI LA REGLA ', rule, 'ES EJECUTABLE: ', ret);
    return ret;
  }

  isProcedureExecutable(rule: RuleDTO): boolean {
    return this.isRuleExecutable(rule) && rule.type === RuleTypes.PROCEDURE;
  }

  isProcedureAuditable(rule: RuleDTO): boolean {
    let ret: boolean;
    ret =
      (rule.state === RuleStates.COMPLETED ||
        rule.state === RuleStates.PROCESSING) &&
      rule.type === RuleTypes.PROCEDURE &&
      rule.procedure !== undefined;
    return ret;
  }

  // Si la regla es de tipo task se invoca a la pantalla correspondiente.
  // En caso contrario se ejecuta la acción y se devuelve un observable.
  // Para invocar a este metodo hay que comprobar si hay objeto de retorno y subscribirse al mismo si existe.
  executeRule(rule: RuleDTO): Observable<any> {
    if (rule.locked === undefined || !rule.locked) {
      return this.internalRefreshRule(rule)
        .pipe(
          flatMap(
            _rule => {

              if (_rule.type === RuleTypes.TASK) {
                if (rule.task) {
                  this.taskService.openTask(_rule.task);
                } else {
                  this.uiService.showMessage(this.translate.instant('DossierActions.error.general'));
                  console.error(
                    'REGLA EJECUTABLE DE TIPO TASK SIN TAREA ASOCIADA',
                    _rule

                  );
                }
                return of(_rule);
              } else {
                return this.internalExecuteRule(rule.identifier).pipe(catchError(error => {
                  this.plycaErrorHandler.showServerError(error);
                  return throwError(error);
                }));
              }
            }
          )
        );
    } else {
      console.log('NO ES POSIBLE EJECUTAR LA REGLA POR ESTAR BLOQUEADA', rule);
      this.uiService.showMessage(this.translate.instant('DossierActions.error.lockrule'));
      return of(rule);
    }
  }

  private internalRefreshRule(rule: RuleDTO): Observable<RuleDTO> {
    let url = this.configuration.URL_BASE +
      this.configuration.RULES_SERVICE_URL +
      rule.identifier +
      '/refresh?' +
      'name=' + rule.name +
      '&division.identifier=' + rule.division.identifier +
      '&phase.identifier=' + rule.phase.identifier +
      '&iteration=' + rule.iteration +
      '&version=' + rule.version +
      '&argumentsHash=' + rule.argumentsHash;



    return this._http.get<RuleDTO>(url);
  }


  // TODO REVISAR SI HAY QUE PASAR DOSSIER Y DIVISIONES
  executeProcedure(rule: RuleDTO): Observable<any> {
    if (rule.locked === undefined || !rule.locked) {
      console.log('Ejecutando regla', rule);
      let config: DynamicDialogConfig = {
        header: 'Ejecutar regla',
        width: '70%',
        style: { overflow: 'scroll', 'max-height': '80%' }
      };
      config['rule'] = rule;
      //    config['dossier'] = this.dossier;
      //    config['divisiones'] = this.divisiones;
      const ref = this.dialogService.open(procedureComponent, config);
      return ref.onClose;
    } else {
      this.uiService.showMessage(this.translate.instant('DossierActions.error.lockrule'));
      console.log(
        'NO ES POSIBLE EJECUTAR LA REGLA (PROCEDURE) POR ESTAR BLOQUEADA',

        rule
      );
    }
  }

  openAudit(rule: RuleDTO) {
    this.router.navigate(['/audit/' + rule.division.project.identifier], {
      queryParams: {
        procedureId: getSimpleIdentifierFromString(rule.procedure.identifier)
      }
    });
  }

  getRuleIcon(rule: RuleDTO): string {
    let ret = '';

    // gestionar parametro que permite fijar un icono a la regla
    let iconParam = rule?.parameters?.find(param => param.name === 'ICONO')?.value
    if (iconParam) {
      ret += iconParam;
    } else if (rule.type === undefined) {
      // si no tiene tipo no se puede ejecutar todavia (como los cambios de fase que no se cumplen)
      ret += 'far fa-stop-circle';
    } else if (rule.state === RuleStates.COMPLETED) {
      // finalizada
      ret += 'far fa-check-circle text-success font-weight-bold';
    } else if (
      rule.state === RuleStates.PROCESSING ||
      (rule.state === RuleStates.PENDING &&
        rule.type === RuleTypes.TASK &&
        rule.locked)
    ) {
      // en ejecucion (tiene tipo y state processing)
      ret += 'far fa-play-circle text-warning';
      // } else if (rule.state === RuleStates.PROCESSING) { // en ejecucion (tiene tipo y state processing)
      //   ret = 'far fa-pause-circle';
    } else {
      ret += 'far fa-pause-circle'; // ?
    }
    return ret;
  }



  isMandatoryDocument(rule: RuleDTO): boolean {
    const mandatoryParam = rule?.parameters?.find(param => param.name === 'DOC_OBLIGATORIO')?.value;
    return mandatoryParam === 'true';
  }



  public unlockRule(rule: RuleDTO): Observable<any> {

    return this.internalUnlockRule(rule).pipe(catchError(error => {
      this.plycaErrorHandler.showServerError(error);
      return throwError(error);
    }));


  }


  private internalUnlockRule(rule: RuleDTO): Observable<RuleDTO> {

    let availableActions: Array<ActionDTO> = rule.availableActions.actions;
    let url: string;
    for (let action of availableActions) {
      if (action.identifier === ActionTypeDTO.TASK_UNLOCK) {
        let links: Link[];
        links = action.links;
        url = links[0].href;
        break;
      }
    }
    if (url) {
      return this._http.put(url, JSON.stringify(rule)).pipe(
        map(() => {
          console.log('response desbloquear');
          return rule;
        })
      );
    }
  }

  /*
 * METODOS PRIVADOS PARA INVOCACION A LAS APIS
 */

  private internalExecuteRule(idRule: string): Observable<Object> {
    let url = this.configuration.URL_BASE +
      this.configuration.RULES_SERVICE_URL +
      idRule +
      '/execute';
    return this._http.put(url, '');
  }
  /*
    * GENERIC HANDLER FOR ERRORS
    */
  // public handleError(error: HttpErrorResponse): Observable<any> {
  //   console.error('handleError!!!!!!!! ', error);
  //   // return Observable.throw( error.json() as ErrorDTO);
  //   let message = false;
  //   let err: any = error.message;
  //   if (error.status === 404) {
  //     //      err = 'Tarea no disponible';
  //     err = new I18nData('DossierActions.error.noAvailable');
  //     message = true;

  //   } else if (error.status === 500) {

  //     err = new I18nData('General.errorMessage');
  //     message = true;

  //   } else if (error.status === 400 && error.error) {
  //     let errorObj = error.error;
  //     if (errorObj) {
  //       if (errorObj.errors) {
  //         let errores: Array<ValidationExecutionDTO> = errorObj.errors;
  //         err =
  //           this.translate.instant('General.warning') + ':<br><ul>';
  //         errores.forEach((error: ValidationExecutionDTO) => {
  //           err += '<li>' + error.message + '</li>';
  //         });
  //         err += '</ul>';
  //       } else {
  //         if (errorObj.message) {
  //           err = errorObj.message;
  //         }
  //         if (errorObj.cause && errorObj.cause.constraintViolations) {
  //           err += ':<br><ul>';
  //           let constraintViolations: Array<any> =
  //             errorObj.cause.constraintViolations;
  //           constraintViolations.forEach(
  //             (constraintViolation: any) =>
  //               (err += '<li>' + constraintViolation.message + '</li>')
  //           );
  //           err += '</ul>';
  //         }
  //       }
  //       message = true;
  //     }
  //   }
  //   return observableThrowError(err);
  // }
}
